// # Note Editor Component
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../auth/state';
import { db, storage } from '../../auth/firebase';
import { doc, getDoc, updateDoc, arrayUnion, Timestamp, deleteField, setDoc } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Image } from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import Link from '@tiptap/extension-link';
import TextStyle from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import Underline from '@tiptap/extension-underline';
import DashboardHeader from '../../components/DashboardHeader';
import DashboardFooter from '../../components/DashboardFooter';
import { FaSave, FaTimes, FaImage, FaYoutube, FaHeading, FaListUl, FaListOl, FaBold, FaItalic, FaUnderline, FaLink, FaPalette, FaHighlighter, FaCheck, FaTrash, FaIndent, FaOutdent } from 'react-icons/fa';
import { MdFormatColorText, MdFormatSize } from 'react-icons/md';
import './notepage.css';
import NoteMedia from './notemedia';
import cacheManager from '../../auth/cache_manager';

// # Import step icons
import stepTwoIcon from '../../aneo_media/mentee/two.png';

// # Import icons from the notes system
import anatomyIcon from '../library/step1/step1_icons/anatomy.png';
import biochemistryIcon from '../library/step1/step1_icons/biochemistry.png';
import bloodIcon from '../library/step1/step1_icons/blood.png';
import cnsIcon from '../library/step1/step1_icons/cns.png';
import earIcon from '../library/step1/step1_icons/ear.png';
import emergencyIcon from '../library/step1/step1_icons/emergency.png';
import endocrineIcon from '../library/step1/step1_icons/endocrine.png';
import eyeIcon from '../library/step1/step1_icons/eye.png';
import gitIcon from '../library/step1/step1_icons/git.png';
import healthcareIcon from '../library/step1/step1_icons/healthcare.png';
import heartIcon from '../library/step1/step1_icons/heart.png';
import immunologyIcon from '../library/step1/step1_icons/immunology.png';
import infectiousIcon from '../library/step1/step1_icons/infectious.png';
import kidneyIcon from '../library/step1/step1_icons/kidney.png';
import lungsIcon from '../library/step1/step1_icons/lungs.png';
import musculoskeletalIcon from '../library/step1/step1_icons/musculoskeletal.png';
import newbornIcon from '../library/step1/step1_icons/newborn.png';
import pathologyIcon from '../library/step1/step1_icons/pathology.png';
import pharmaIcon from '../library/step1/step1_icons/pharma.png';
import psychiatryIcon from '../library/step1/step1_icons/psychiatry.png';
import publicHealthIcon from '../library/step1/step1_icons/public_health.png';
import qualityIcon from '../library/step1/step1_icons/quality.png';
import reproductiveIcon from '../library/step1/step1_icons/reproductive-system.png';
import revisionIcon from '../library/step1/step1_icons/revision.png';
import skinIcon from '../library/step1/step1_icons/skin.png';
import statisticsIcon from '../library/step1/step1_icons/statistics.png';
import surgeryIcon from '../library/step1/step1_icons/surgery.png';
import wellPatientIcon from '../library/step1/step1_icons/well_patient.png';

// # Main Note Editor Component
const NotePage = () => {
  const { stepId, chapterId, noteId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [title, setTitle] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [chapterIcon, setChapterIcon] = useState(null);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [noteMedia, setNoteMedia] = useState([]);
  const noteMediaRef = useRef(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [saved, setSaved] = useState(false);
  
  // # Determine if this is a Step 2 chapter
  const isStep2 = stepId.includes('Step 2');

  // # Load theme preference from user data on initial render
  useEffect(() => {
    const loadThemePreference = async () => {
      if (user && user.uid) {
        // # Try to get from cache first
        const userData = cacheManager.get(`user_${user.uid}`);
        if (userData && userData.themes && userData.themes.gen_theme !== undefined) {
          setIsDarkMode(userData.themes.gen_theme === 'dark');
        }
      }
    };
    
    loadThemePreference();
  }, [user]);

  // # Handle theme toggle
  const handleThemeToggle = async () => {
    try {
      const newTheme = !isDarkMode;
      setIsDarkMode(newTheme);
      
      // # Update theme in Firebase if user is logged in
      if (user && user.uid) {
        // # Update the user document
        await updateDoc(doc(db, 'users', user.uid), {
          'themes.gen_theme': newTheme ? 'dark' : 'light'
        });
        
        // # Update cache manager
        const userData = cacheManager.get(`user_${user.uid}`);
        if (userData) {
          const updatedUserData = {
            ...userData,
            themes: {
              ...(userData.themes || {}),
              gen_theme: newTheme ? 'dark' : 'light'
            }
          };
          cacheManager.set(`user_${user.uid}`, updatedUserData);
        }
      }
    } catch (error) {
      console.error('Error updating theme preference:', error);
    }
  };

  // # Format display title for all chapters - same as chapter_notes.js
  const formatDisplayTitle = (title) => {
    if (!title) return '';
    
    // # Replace underscores with spaces
    let formattedTitle = title.replace(/_/g, ' ');
    
    // # Handle case where title is all uppercase with underscores (Step 2 format)
    if (/^[A-Z,&\s]+$/.test(formattedTitle)) {
      // # Convert to title case (first letter of each word uppercase, rest lowercase)
      formattedTitle = formattedTitle
        .split(' ')
        .map(word => {
          // # Keep special characters as is
          if (word === '&') return '&';
          // # Convert to title case
          return word.charAt(0) + word.slice(1).toLowerCase();
        })
        .join(' ');
    } 
    // # Handle lowercase with underscores (Step 1 format)
    else if (/^[a-z_&\s]+$/.test(title)) {
      // # Convert to title case
      formattedTitle = formattedTitle
        .split(' ')
        .map(word => {
          // # Keep special characters as is
          if (word === '&') return '&';
          // # Convert first character to uppercase
          return word.charAt(0).toUpperCase() + word.slice(1);
        })
        .join(' ');
    }
    
    // # Ensure proper spacing around ampersands and commas
    formattedTitle = formattedTitle
      .replace(/\s*&\s*/g, ' & ')
      .replace(/\s*,\s*/g, ', ');
    
    return formattedTitle;
  };
  
  // # Get chapter icon based on chapter name - same as chapter_notes.js
  const getChapterIcon = (chapterName) => {
    // # Define chapter mapping for both Step 1 and Step 2 based on my_notes.js
    const step1ChapterMap = {
      'biochemistry': biochemistryIcon,
      'immunology': immunologyIcon,
      'microbiology': infectiousIcon,
      'general_pathology': pathologyIcon,
      'pharmacology': pharmaIcon,
      'public_health_sciences': publicHealthIcon,
      'anatomy_histology': anatomyIcon,
      'cardiovascular': heartIcon,
      'endocrine': endocrineIcon,
      'gastrointestinal': gitIcon,
      'hematology_oncology': bloodIcon,
      'musculoskeletal_connective_tissue': musculoskeletalIcon,
      'dermatology': skinIcon,
      'neurology': cnsIcon,
      'special_senses': eyeIcon,
      'psychiatry': psychiatryIcon,
      'renal': kidneyIcon,
      'reproductive': reproductiveIcon,
      'respiratory': lungsIcon,
      'rapid_review': revisionIcon,
      'sketchy_micro': infectiousIcon
    };

    const step2ChapterMap = {
      'cardiovascular': heartIcon,
      'respiratory_ent': lungsIcon,
      'renal_urology': kidneyIcon,
      'endocrine': endocrineIcon,
      'hematology_oncology': bloodIcon,
      'immunology': immunologyIcon,
      'git': gitIcon,
      'musculoskeletal': musculoskeletalIcon,
      'neurology': cnsIcon,
      'ophthalmology': eyeIcon,
      'psychiatry': psychiatryIcon,
      'dermatology': skinIcon,
      'gynecology_obstetrics': reproductiveIcon,
      'neonate_infant': newbornIcon,
      'genetics': biochemistryIcon,
      'infectious_disease': infectiousIcon,
      'surgery_anesthesia': surgeryIcon,
      'emergency_trauma_toxicology': emergencyIcon,
      'the_well_patient': wellPatientIcon,
      'biostatistics_epidemiology': statisticsIcon,
      'ethics_communication': publicHealthIcon,
      'healthcare_delivery': healthcareIcon,
      'quality_safety': qualityIcon,
      'pharmacology_review': pharmaIcon
    };

    // # Normalize the chapter name for matching
    const normalizedName = chapterName.toLowerCase().replace(/[^a-z0-9]/g, '_');
    
    // # First try exact match from the appropriate map based on step
    const chapterMap = isStep2 ? step2ChapterMap : step1ChapterMap;
    if (chapterMap[normalizedName]) {
      return chapterMap[normalizedName];
    }
    
    // # If no exact match, try partial match
    for (const [key, value] of Object.entries(chapterMap)) {
      if (normalizedName.includes(key) || key.includes(normalizedName)) {
        return value;
      }
    }
    
    // # If still no match, check the other step's map
    const otherMap = isStep2 ? step1ChapterMap : step2ChapterMap;
    for (const [key, value] of Object.entries(otherMap)) {
      if (normalizedName.includes(key) || key.includes(normalizedName)) {
        return value;
      }
    }
    
    // # Use these common keywords for further matching
    const iconKeywords = {
      'heart': heartIcon,
      'cardio': heartIcon,
      'lung': lungsIcon,
      'resp': lungsIcon,
      'kidney': kidneyIcon,
      'renal': kidneyIcon,
      'endocrine': endocrineIcon,
      'blood': bloodIcon,
      'hemat': bloodIcon,
      'oncol': bloodIcon,
      'immun': immunologyIcon,
      'gastro': gitIcon,
      'intestin': gitIcon,
      'muscle': musculoskeletalIcon,
      'skeletal': musculoskeletalIcon,
      'connect': musculoskeletalIcon,
      'neuro': cnsIcon,
      'eye': eyeIcon,
      'ophthal': eyeIcon,
      'psych': psychiatryIcon,
      'mental': psychiatryIcon,
      'skin': skinIcon,
      'derma': skinIcon,
      'repro': reproductiveIcon,
      'gynec': reproductiveIcon,
      'obstet': reproductiveIcon,
      'infant': newbornIcon,
      'neonate': newbornIcon,
      'baby': newbornIcon,
      'gene': biochemistryIcon,
      'infect': infectiousIcon,
      'micro': infectiousIcon,
      'surg': surgeryIcon,
      'anesth': surgeryIcon,
      'emerg': emergencyIcon,
      'trauma': emergencyIcon,
      'toxic': emergencyIcon,
      'well': wellPatientIcon,
      'prevent': wellPatientIcon,
      'stat': statisticsIcon,
      'epidem': statisticsIcon,
      'ethic': publicHealthIcon,
      'commun': publicHealthIcon,
      'health': healthcareIcon,
      'quality': qualityIcon,
      'safety': qualityIcon,
      'pharma': pharmaIcon
    };
    
    // # Check for keyword matches
    for (const [key, icon] of Object.entries(iconKeywords)) {
      if (normalizedName.includes(key)) {
        return icon;
      }
    }
    
    // # Default icon if no match found
    return isStep2 ? stepTwoIcon : revisionIcon;
  };

  // # Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // # Only close if clicking outside any dropdown
      if (activeDropdown && !event.target.closest('.npg_dropdown')) {
        setActiveDropdown(null);
      }
    };

    // # Add event listener to document
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      // # Clean up event listener
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [activeDropdown]);

  // # Define editor configuration with TipTap
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // # Configure ordered list to use different markers for nesting levels
        orderedList: {
          keepMarks: true,
          keepAttributes: true,
        },
      }),
      Image,
      Link.configure({
        openOnClick: true, // # Allow link clicks
        HTMLAttributes: {
          target: '_blank',
          rel: 'noopener noreferrer',
          class: 'npg_link',
        },
        // # Custom validation function to ensure links open in new tab
        validate: url => /^https?:\/\//.test(url),
      }),
      Placeholder.configure({
        placeholder: 'Start writing your note here...',
      }),
      TextStyle,
      Color,
      Highlight.configure({
        multicolor: true,
      }),
      Underline,
    ],
    content: '',
    autofocus: true,
    editable: true,
    injectCSS: true,
    // # Enable handling of DOM events
    editorProps: {
      attributes: {
        class: 'npg_editor_main',
      },
      handleClick: (view, pos, event) => {
        const { state } = view;
        const link = state.doc.nodeAt(pos)?.marks.find(mark => mark.type.name === 'link');
        
        // # If clicked on a link, open in a new tab
        if (link) {
          const href = link.attrs.href;
          if (href) {
            window.open(href, '_blank', 'noopener,noreferrer');
            return true; // # Event handled
          }
        }
        
        // # Continue with default handling
        return false;
      },
    },
  });

  // # Set chapter icon using the new getChapterIcon function
  useEffect(() => {
    if (!chapterId) return;
    
    // # Use the consistent icon getter function
    setChapterIcon(getChapterIcon(chapterId));
  }, [chapterId]);

  // # Load existing note data if editing
  useEffect(() => {
    const loadNote = async () => {
      if (!user || !noteId) return;
      
      try {
        // # Format the step key for Firestore - replace hyphens with underscores
        const stepKey = `${stepId}_notes`.replace(/[\s-]+/g, '_').toLowerCase();
        const chapterKey = `${chapterId}`.replace(/[\s-]+/g, '_').toLowerCase();
        
        // # Get the user's notes document
        const notesRef = doc(db, 'notes', user.uid);
        const notesDoc = await getDoc(notesRef);
        
        if (notesDoc.exists()) {
          const notesData = notesDoc.data();
          
          // # Check if note exists in the nested structure
          if (notesData[stepKey] && 
              notesData[stepKey][chapterKey] && 
              notesData[stepKey][chapterKey][noteId]) {
              
            const noteToEdit = notesData[stepKey][chapterKey][noteId];
            
            if (noteToEdit) {
              setTitle(noteToEdit.title);
              
              // # Set editor content if editor is initialized
              if (editor) {
                editor.commands.setContent(noteToEdit.content);
              }
              
              // # Load media if it exists
              if (noteToEdit.media && Array.isArray(noteToEdit.media)) {
                setNoteMedia(noteToEdit.media);
              }
            }
          }
        }
      } catch (error) {
        console.error('Error loading note:', error);
      }
    };
    
    if (editor) {
      loadNote();
    }
  }, [user, stepId, chapterId, noteId, editor]);

  // # Handle media add
  const handleMediaAdd = (media) => {
    setNoteMedia((prevMedia) => [...prevMedia, media]);
  };
  
  // # Handle media remove
  const handleMediaRemove = (mediaId) => {
    setNoteMedia((prevMedia) => prevMedia.filter((media) => media.id !== mediaId));
  };

  // # Generate unique note ID
  const generateNoteId = () => {
    const datestamp = new Date().toISOString().split('T')[0].replace(/-/g, '');
    const unique15Chars = Math.random().toString(36).substring(2, 10) + 
                         Math.random().toString(36).substring(2, 9);
    return `mynt_${datestamp}_${unique15Chars}`;
  };

  // # Save the note
  const saveNote = async () => {
    if (!user || !editor || !title.trim()) {
      alert('Please enter a title for your note');
      return;
    }
    
    try {
      setSaving(true);
      
      // # Check if any images are still being uploaded
      const isUploadingMedia = noteMedia.some(media => media.tempUpload === true);
      
      if (isUploadingMedia) {
        alert('Please wait for image uploads to complete before saving');
        setSaving(false);
        return;
      }
      
      // # Clean media array to ensure no temporary files or Blob objects
      const finalMedia = noteMedia.map(media => {
        // # Create a clean media object with validated properties
        const cleanMedia = {
          type: media.type || 'unknown',
          url: media.url || '',
          id: media.id || `media_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`
        };
        
        // # Only include path if it exists and is not undefined
        if (media.path) {
          cleanMedia.path = media.path;
        }
        
        return cleanMedia;
      });
      
      // # Get the current editor content
      const content = editor.getHTML();
      
      // # Format the keys for Firestore - step level and chapter level
      // # Make sure to replace hyphens with underscores consistently
      const stepKey = `${stepId}_notes`.replace(/[\s-]+/g, '_').toLowerCase();
      const chapterKey = `${chapterId}`.replace(/[\s-]+/g, '_').toLowerCase();
      
      // # Get the user's notes document
      const notesRef = doc(db, 'notes', user.uid);
      const notesDoc = await getDoc(notesRef);
      
      const now = Timestamp.now();
      
      if (notesDoc.exists()) {
        const notesData = notesDoc.data();
        const noteIdToUse = noteId || generateNoteId();
        
        // # If editing an existing note, get current media to check for deletions
        let currentMedia = [];
        if (noteId && notesData[stepKey]?.[chapterKey]?.[noteId]?.media) {
          currentMedia = notesData[stepKey][chapterKey][noteId].media;
        }
        
        // # Check for media items that were removed and need to be deleted from storage
        const removedMedia = currentMedia.filter(oldMedia => 
          oldMedia.type === 'image' && 
          oldMedia.path && 
          !finalMedia.some(newMedia => newMedia.id === oldMedia.id)
        );
        
        // # Delete removed media items from storage
        for (const media of removedMedia) {
          try {
            const fileRef = ref(storage, media.path);
            await deleteObject(fileRef);
            console.log(`Deleted removed media file: ${media.path}`);
          } catch (error) {
            console.error(`Error deleting removed media file: ${media.path}`, error);
            // # Continue with save even if deletion fails
          }
        }
        
        // # Prepare the note data
        const noteData = {
          title: title.trim(),
          content: content,
          media: finalMedia,
          createdAt: notesData[stepKey]?.[chapterKey]?.[noteIdToUse]?.createdAt || now,
          updatedAt: now
        };
        
        // # Update the note in Firestore
        await updateDoc(notesRef, {
          [`${stepKey}.${chapterKey}.${noteIdToUse}`]: noteData,
          // # Initialize or increment notesCount if this is a new note
          notesCount: noteId
            ? (notesData.notesCount || 0) // Keep count the same if editing
            : ((notesData.notesCount || 0) + 1) // Increment if new note
        });
        
        console.log('Note saved successfully');
        
        // # If this is a new note, redirect to edit mode with the new ID
        if (!noteId) {
          // # Get the current path to determine if we're in admin mode
          const isAdmin = window.location.pathname.startsWith('/admin');
          const basePath = isAdmin ? '/admin/notes' : '/notes';
          
          // # Set saved state to show success message
          setSaved(true);
          
          // # Redirect to the edit page for the new note
          navigate(`${basePath}/${stepId}/${chapterId}/edit/${noteIdToUse}`, { replace: true });
        } else {
          setSaved(true);
          setTimeout(() => setSaved(false), 3000);
        }
      } else {
        // # Create a new notes document for the user
        const noteIdToUse = noteId || generateNoteId();
        
        // # Prepare the note data
        const noteData = {
          title: title.trim(),
          content: content,
          media: finalMedia,
          createdAt: now,
          updatedAt: now
        };
        
        // # Create nested structure for the new note
        const notesStructure = {
          [stepKey]: {
            [chapterKey]: {
              [noteIdToUse]: noteData
            }
          },
          notesCount: 1 // # Initialize notes count
        };
        
        // # Create the notes document
        await setDoc(doc(db, 'notes', user.uid), notesStructure);
        
        console.log('Note saved successfully (new user notes document created)');
        
        // # Set saved state to show success message
        setSaved(true);
        
        // # Redirect to edit mode with the new ID
        const isAdmin = window.location.pathname.startsWith('/admin');
        const basePath = isAdmin ? '/admin/notes' : '/notes';
        navigate(`${basePath}/${stepId}/${chapterId}/edit/${noteIdToUse}`, { replace: true });
      }
    } catch (error) {
      console.error('Error saving note:', error);
      alert('Error saving note. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // # Handle return to chapter notes
  const handleCancel = () => {
    // # Clean up any temporary image object URLs
    noteMedia.forEach(media => {
      if (media.type === 'image' && media.isTemp) {
        try {
          // # Revoke object URL to prevent memory leaks
          URL.revokeObjectURL(media.url);
          console.log(`Revoked temporary URL for image: ${media.id}`);
        } catch (error) {
          console.error(`Error revoking object URL: ${error}`);
          // # Continue cleanup even if revocation fails
        }
      }
    });
    
    // # Navigate back to chapter notes
    const isAdmin = window.location.pathname.startsWith('/admin');
    const basePath = isAdmin ? '/admin/notes' : '/notes';
    navigate(`${basePath}/${stepId}/${chapterId}`);
  };

  // # Delete note with proper cleanup
  const handleDeleteNote = async () => {
    if (!user || !noteId || deleting) return;
    
    // # Close the confirmation dialog
    setShowConfirmDialog(false);
    
    try {
      setDeleting(true);
      
      // # Format keys for Firestore - replace hyphens with underscores
      const stepKey = `${stepId}_notes`.replace(/[\s-]+/g, '_').toLowerCase();
      const chapterKey = `${chapterId}`.replace(/[\s-]+/g, '_').toLowerCase();
      
      // # Get the note data to check for media
      const notesRef = doc(db, 'notes', user.uid);
      const notesDoc = await getDoc(notesRef);
      
      if (notesDoc.exists()) {
        const notesData = notesDoc.data();
        
        // # Check if the note exists
        if (notesData[stepKey] && 
            notesData[stepKey][chapterKey] && 
            notesData[stepKey][chapterKey][noteId]) {
          
          const noteToDelete = notesData[stepKey][chapterKey][noteId];
          let mediaDeleteErrors = 0;
          
          // # Delete associated media from storage if it exists
          if (noteToDelete.media && Array.isArray(noteToDelete.media)) {
            // # Process each media item (images need to be deleted from storage)
            for (const media of noteToDelete.media) {
              if (media.type === 'image' && media.path) {
                try {
                  // # Create reference to the file and delete it
                  const fileRef = ref(storage, media.path);
                  await deleteObject(fileRef);
                  console.log(`Deleted media file: ${media.path}`);
                } catch (error) {
                  console.error(`Error deleting media file: ${media.path}`, error);
                  mediaDeleteErrors++;
                  // # Continue with note deletion even if media deletion fails
                }
              }
            }
          }
          
          // # Log warning if there were media deletion errors
          if (mediaDeleteErrors > 0) {
            console.warn(`Failed to delete ${mediaDeleteErrors} media files while deleting note ${noteId}`);
          }
          
          // # Delete the note from Firestore
          await updateDoc(notesRef, {
            [`${stepKey}.${chapterKey}.${noteId}`]: deleteField(),
            notesCount: Math.max((notesData.notesCount || 0) - 1, 0) // # Decrement note count, minimum 0
          });
          
          console.log(`Note ${noteId} successfully deleted`);
          
          // # Navigate back to the chapter notes
          const isAdmin = window.location.pathname.startsWith('/admin');
          const basePath = isAdmin ? '/admin/notes' : '/notes';
          navigate(`${basePath}/${stepId}/${chapterId}`);
        } else {
          console.error(`Note ${noteId} not found in path: ${stepKey}.${chapterKey}`);
          alert('Error: Note not found. It may have been already deleted.');
          setDeleting(false);
        }
      } else {
        console.error('Notes document not found for user');
        alert('Error: User notes not found. Please try again later.');
        setDeleting(false);
      }
    } catch (error) {
      console.error('Error deleting note:', error);
      alert('Error deleting note. Please try again.');
      setDeleting(false);
    }
  };

  // # Show delete confirmation dialog
  const showDeleteConfirmation = () => {
    setShowConfirmDialog(true);
  };
  
  // # Handle cancel delete
  const handleCancelDelete = () => {
    setShowConfirmDialog(false);
  };
  
  // # Custom confirmation dialog component
  const ConfirmDeleteDialog = ({ isOpen, onConfirm, onCancel }) => {
    if (!isOpen) return null;
    
    return (
      <div className="np_confirm_overlay" onClick={onCancel}>
        <div className="np_confirm_dialog" onClick={(e) => e.stopPropagation()}>
          <h3 className="np_confirm_title">Delete Note</h3>
          <p className="np_confirm_message">
            Are you sure you want to delete this note? This action cannot be undone.
          </p>
          <div className="np_confirm_buttons">
            <button className="np_confirm_cancel" onClick={onCancel}>
              Cancel
            </button>
            <button className="np_confirm_delete" onClick={onConfirm}>
              Delete
            </button>
          </div>
        </div>
      </div>
    );
  };

  // # Editor menu buttons
  const MenuBar = () => {
    if (!editor) {
      return null;
    }

    // # Toggle dropdown function
    const toggleDropdown = (dropdownName, event) => {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      setActiveDropdown(activeDropdown === dropdownName ? null : dropdownName);
    };

    // # Close dropdown after selecting an option
    const closeDropdown = (event) => {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      setActiveDropdown(null);
    };

    // # Define color palette
    const textColors = [
      '#ff6b6b',  // # Pastel red
      '#ffa06b',  // # Pastel orange
      '#ffd166',  // # Pastel yellow
      '#06d6a0',  // # Pastel green
      '#118ab2',  // # Pastel blue
      '#7209b7',  // # Pastel purple
      '#3a0ca3',  // # Pastel indigo
    ];
    
    // # Define highlight colors
    const highlightColors = [
      '#ffdb99',  // # Light yellow
      '#c1f8cf',  // # Light green
      '#b9d9eb',  // # Light blue
      '#f9c0c0',  // # Light pink
    ];

    return (
      <div className="npg_editor_menu">
        <button
          onMouseDown={(e) => {
            e.preventDefault();
            editor.chain().focus().toggleBold().run();
          }}
          className={editor.isActive('bold') ? 'npg_is_active' : ''}
          title="Bold"
        >
          <FaBold />
        </button>
        
        <button
          onMouseDown={(e) => {
            e.preventDefault();
            editor.chain().focus().toggleItalic().run();
          }}
          className={editor.isActive('italic') ? 'npg_is_active' : ''}
          title="Italic"
        >
          <FaItalic />
        </button>
        
        <button
          onMouseDown={(e) => {
            e.preventDefault();
            editor.chain().focus().toggleUnderline().run();
          }}
          className={editor.isActive('underline') ? 'npg_is_active' : ''}
          title="Underline"
        >
          <FaUnderline />
        </button>
        
        {/* # Text color dropdown */}
        <div className={`npg_dropdown ${activeDropdown === 'textColor' ? 'npg_dropdown_active' : ''}`}>
          <button
            className="npg_dropdown_button"
            title="Text Color"
            onMouseDown={(e) => toggleDropdown('textColor', e)}
            aria-expanded={activeDropdown === 'textColor'}
            aria-haspopup="true"
          >
            <MdFormatColorText />
          </button>
          <div className="npg_dropdown_content npg_color_palette">
            {textColors.map((color) => (
              <button
                key={color}
                className="npg_color_button"
                style={{ backgroundColor: color }}
                onMouseDown={(e) => {
                  editor.chain().focus().setColor(color).run();
                  closeDropdown(e);
                }}
                title={`Set text color to ${color}`}
              />
            ))}
            <button
              className="npg_color_button npg_color_remove"
              onMouseDown={(e) => {
                editor.chain().focus().unsetColor().run();
                closeDropdown(e);
              }}
              title="Remove color"
            >
              <span>✕</span>
            </button>
          </div>
        </div>
        
        {/* # Highlight dropdown */}
        <div className={`npg_dropdown ${activeDropdown === 'highlight' ? 'npg_dropdown_active' : ''}`}>
          <button
            className="npg_dropdown_button"
            title="Highlight"
            onMouseDown={(e) => toggleDropdown('highlight', e)}
            aria-expanded={activeDropdown === 'highlight'}
            aria-haspopup="true"
          >
            <FaHighlighter />
          </button>
          <div className="npg_dropdown_content npg_color_palette">
            {highlightColors.map((color) => (
              <button
                key={color}
                className="npg_color_button"
                style={{ backgroundColor: color }}
                onMouseDown={(e) => {
                  editor.chain().focus().toggleHighlight({ color }).run();
                  closeDropdown(e);
                }}
                title={`Highlight text with ${color}`}
              />
            ))}
            <button
              className="npg_color_button npg_color_remove"
              onMouseDown={(e) => {
                editor.chain().focus().unsetHighlight().run();
                closeDropdown(e);
              }}
              title="Remove highlight"
            >
              <span>✕</span>
            </button>
          </div>
        </div>
        
        {/* # Headings dropdown */}
        <div className={`npg_dropdown ${activeDropdown === 'headings' ? 'npg_dropdown_active' : ''}`}>
          <button
            className="npg_dropdown_button"
            title="Heading"
            onMouseDown={(e) => toggleDropdown('headings', e)}
            aria-expanded={activeDropdown === 'headings'}
            aria-haspopup="true"
          >
            <FaHeading />
          </button>
          <div className="npg_dropdown_content">
            <button
              className={editor.isActive('heading', { level: 1 }) ? 'npg_is_active' : ''}
              onMouseDown={(e) => {
                editor.chain().focus().toggleHeading({ level: 1 }).run();
                closeDropdown(e);
              }}
            >
              <span>Heading 1</span>
              {editor.isActive('heading', { level: 1 }) && <FaCheck className="npg_check_icon" />}
            </button>
            <button
              className={editor.isActive('heading', { level: 2 }) ? 'npg_is_active' : ''}
              onMouseDown={(e) => {
                editor.chain().focus().toggleHeading({ level: 2 }).run();
                closeDropdown(e);
              }}
            >
              <span>Heading 2</span>
              {editor.isActive('heading', { level: 2 }) && <FaCheck className="npg_check_icon" />}
            </button>
            <button
              className={editor.isActive('heading', { level: 3 }) ? 'npg_is_active' : ''}
              onMouseDown={(e) => {
                editor.chain().focus().toggleHeading({ level: 3 }).run();
                closeDropdown(e);
              }}
            >
              <span>Heading 3</span>
              {editor.isActive('heading', { level: 3 }) && <FaCheck className="npg_check_icon" />}
            </button>
            <button
              className={editor.isActive('paragraph') ? 'npg_is_active' : ''}
              onMouseDown={(e) => {
                editor.chain().focus().setParagraph().run();
                closeDropdown(e);
              }}
            >
              <span>Normal text</span>
              {editor.isActive('paragraph') && <FaCheck className="npg_check_icon" />}
            </button>
          </div>
        </div>
        
        <button
          onMouseDown={(e) => {
            e.preventDefault();
            editor.chain().focus().toggleBulletList().run();
          }}
          className={editor.isActive('bulletList') ? 'npg_is_active' : ''}
          title="Bullet List"
        >
          <FaListUl />
        </button>
        
        <button
          onMouseDown={(e) => {
            e.preventDefault();
            editor.chain().focus().toggleOrderedList().run();
          }}
          className={editor.isActive('orderedList') ? 'npg_is_active' : ''}
          title="Numbered List"
        >
          <FaListOl />
        </button>
        
        {/* # Update indent/outdent buttons */}
        <button
          onMouseDown={(e) => {
            e.preventDefault();
            editor.chain().focus().sinkListItem('listItem').run();
          }}
          className={`npg_indent_button ${!editor.can().sinkListItem('listItem') ? 'npg_button_disabled' : ''}`}
          disabled={!editor.can().sinkListItem('listItem')}
          title="Indent"
        >
          <FaIndent />
        </button>
        
        <button
          onMouseDown={(e) => {
            e.preventDefault();
            editor.chain().focus().liftListItem('listItem').run();
          }}
          className={`npg_outdent_button ${!editor.can().liftListItem('listItem') ? 'npg_button_disabled' : ''}`}
          disabled={!editor.can().liftListItem('listItem')}
          title="Outdent"
        >
          <FaOutdent />
        </button>
        
        <button
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setShowLinkDialog(true);
          }}
          className={editor.isActive('link') ? 'npg_is_active' : ''}
          title="Link"
        >
          <FaLink />
        </button>
        
        {/* # Clear formatting button */}
        <button
          onMouseDown={(e) => {
            e.preventDefault();
            editor.chain().focus().unsetAllMarks().clearNodes().run();
          }}
          title="Clear Formatting"
          className="npg_clear_formatting"
        >
          <MdFormatSize />
        </button>
      </div>
    );
  };

  // # Link dialog component
  const LinkDialog = ({ isOpen, onClose, onSubmit }) => {
    const [url, setUrl] = useState('');
    
    // # Handle form submission
    const handleSubmit = (e) => {
      e.preventDefault();
      if (url.trim()) {
        onSubmit(url);
        setUrl('');
        onClose();
      }
    };
    
    if (!isOpen) return null;
    
    return (
      <div className="npg_link_dialog_overlay" onClick={onClose}>
        <div className="npg_link_dialog" onClick={(e) => e.stopPropagation()}>
          <h3>Insert Link</h3>
          <form onSubmit={handleSubmit}>
            <div className="npg_link_dialog_input">
              <label htmlFor="url">URL</label>
              <input 
                type="text" 
                id="url" 
                value={url} 
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://example.com"
                autoFocus
              />
            </div>
            <div className="npg_link_dialog_note">
              Links will open in a new tab
            </div>
            <div className="npg_link_dialog_buttons">
              <button type="button" onClick={onClose}>Cancel</button>
              <button type="submit">Insert</button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  return (
    <div className={`npg_container ${isDarkMode ? 'npg_dark_mode' : ''}`}>
      <DashboardHeader isDarkMode={isDarkMode} onThemeToggle={handleThemeToggle} />
      
      <div className="npg_main">
        {/* Chapter title bar */}
        <div className="npg_chapter_header">
          <div className="npg_chapter_icon">
            {chapterIcon && <img src={chapterIcon} alt={chapterId} />}
          </div>
          <div className="npg_chapter_title">
            <h2>{`${stepId} ${formatDisplayTitle(chapterId)}`}</h2>
          </div>
        </div>
        
        {/* Note title input */}
        <div className="npg_title_container">
          <input
            type="text"
            className="npg_title_input"
            placeholder="Enter note title..."
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>
        
        {/* Editor toolbar */}
        <MenuBar />
        
        {/* Main editor */}
        <div className="npg_editor_container">
          <EditorContent editor={editor} className="npg_editor_content" />
        </div>
        
        {/* Note Media Component - handles image and video embedding outside the editor */}
        <NoteMedia
          ref={noteMediaRef}
          onMediaAdd={handleMediaAdd}
          onMediaRemove={handleMediaRemove}
          noteMedia={noteMedia}
          noteId={noteId}
        />
        
        {/* Action buttons */}
        <div className="npg_actions">
          {noteId && (
            <button 
              className="npg_delete_button" 
              onClick={showDeleteConfirmation}
              disabled={saving || deleting}
            >
              <FaTrash /> {deleting ? 'Deleting...' : 'Delete Note'}
            </button>
          )}
          <button 
            className="npg_cancel_button" 
            onClick={handleCancel}
            disabled={saving || deleting}
          >
            <FaTimes /> Cancel
          </button>
          <button 
            className="npg_save_button" 
            onClick={saveNote}
            disabled={saving || deleting}
          >
            <FaSave /> {saving ? 'Saving...' : 'Save Note'}
          </button>
        </div>
        
        {/* Save confirmation message - fixed position for better visibility */}
        {saved && (
          <div className="npg_save_notification">
            <div className="npg_save_confirmation">
              <FaCheck /> Note saved successfully
            </div>
          </div>
        )}
        
        {/* Link Dialog */}
        <LinkDialog 
          isOpen={showLinkDialog} 
          onClose={() => setShowLinkDialog(false)} 
          onSubmit={(url) => {
            // # Ensure protocol is included for external links
            let finalUrl = url;
            if (!/^https?:\/\//i.test(url) && url.length > 0) {
              finalUrl = `https://${url}`;
            }
            
            // # Set link with target attribute if needed
            editor.chain().focus().setLink({ 
              href: finalUrl,
              target: '_blank', // # Always open in new tab
              rel: 'noopener noreferrer',
            }).run();
          }} 
        />
        
        {/* Confirmation Dialog */}
        <ConfirmDeleteDialog 
          isOpen={showConfirmDialog} 
          onConfirm={handleDeleteNote} 
          onCancel={handleCancelDelete} 
        />
      </div>
      
      <DashboardFooter isDarkMode={isDarkMode} />
    </div>
  );
};

export default NotePage;
